/**
 * AUTO-05: dedicated Automation Rules admin UI.
 *
 * Full CRUD for automation rules with inline condition/action builders,
 * drag-free reorder (up/down arrows), and per-rule execution log viewer.
 */
import * as React from 'react';
import { api } from '@/lib/api-client';
import { useI18n } from '@/lib/i18n';
import { useAuth } from '@/lib/auth';
import { formatDateTime } from '@/lib/date-format';
import {
  Button, Card, CardContent, CardHeader, CardTitle, DropdownSelect, EmptyState, ErrorState, Input, PageHeader, SkeletonRow, useConfirm,
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui';

// --- Types ---

interface Condition { field: string; operator: string; value: string }
interface Action { type: string; value: string }

interface AutomationRule {
  id: string;
  name: string;
  description: string | null;
  trigger: 'ticket_creation' | 'ticket_update' | 'time_trigger';
  conditions: Condition[];
  actions: Action[];
  position: number;
  isActive: boolean;
  stopProcessing: boolean;
  hitCount: number;
  createdAt: string;
}

interface Execution {
  id: string;
  ruleId: string;
  ticketId: string;
  matched: boolean;
  actionsApplied: string[];
  createdAt: string;
  ticket: { id: string; ticketNumber: string; subject: string };
}

// --- Constants ---

const TRIGGERS: { value: AutomationRule['trigger']; label: string }[] = [
  { value: 'ticket_creation', label: 'Ticket creation' },
  { value: 'ticket_update', label: 'Ticket update' },
  { value: 'time_trigger', label: 'Time-based' },
];

const OPERATORS = [
  'is', 'is_not', 'in', 'not_in', 'contains', 'not_contains',
  'starts_with', 'ends_with', 'is_empty', 'is_not_empty',
  'greater_than', 'less_than',
];

const CONDITION_FIELDS = [
  'status', 'priority', 'source', 'subject', 'groupId',
  'assigneeId', 'requesterId', 'companyId', 'ticketTypeId',
];

const ACTION_TYPES = [
  'set_priority', 'set_status', 'assign_group', 'assign_agent',
  'add_tag', 'send_notification', 'send_email',
];

// --- Form state ---

type FormState = {
  name: string;
  description: string;
  trigger: AutomationRule['trigger'];
  conditions: Condition[];
  actions: Action[];
  isActive: boolean;
  stopProcessing: boolean;
};

const EMPTY_FORM: FormState = {
  name: '', description: '', trigger: 'ticket_creation',
  conditions: [{ field: 'status', operator: 'is', value: '' }],
  actions: [{ type: 'set_priority', value: '' }],
  isActive: true, stopProcessing: false,
};

function toForm(rule: AutomationRule): FormState {
  return {
    name: rule.name,
    description: rule.description ?? '',
    trigger: rule.trigger,
    conditions: rule.conditions.length ? rule.conditions : [{ field: 'status', operator: 'is', value: '' }],
    actions: rule.actions.length ? rule.actions : [{ type: 'set_priority', value: '' }],
    isActive: rule.isActive,
    stopProcessing: rule.stopProcessing,
  };
}

// --- Component ---

export function AutomationsPage() {
  const { t, tOr } = useI18n();
  const { user } = useAuth();
  const { confirm } = useConfirm();
  const [rules, setRules] = React.useState<AutomationRule[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [editing, setEditing] = React.useState<string | 'new' | null>(null);
  const [form, setForm] = React.useState<FormState>(EMPTY_FORM);
  const [saving, setSaving] = React.useState(false);
  const [logRuleId, setLogRuleId] = React.useState<string | null>(null);
  const [executions, setExecutions] = React.useState<Execution[]>([]);
  const [logLoading, setLogLoading] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true); setError('');
    try {
      const data = await api.get<AutomationRule[]>('/admin/automations');
      setRules(Array.isArray(data) ? data : []);
    } catch (e) { setError(e instanceof Error ? e.message : t('adm.auto.loadFailed')); }
    finally { setLoading(false); }
  }, []);

  React.useEffect(() => { void load(); }, [load]);

  // --- CRUD ---

  function startCreate() { setEditing('new'); setForm(EMPTY_FORM); setLogRuleId(null); }
  function startEdit(rule: AutomationRule) { setEditing(rule.id); setForm(toForm(rule)); setLogRuleId(null); }
  function cancelEdit() { setEditing(null); setForm(EMPTY_FORM); }

  async function save(e: React.FormEvent) {
    e.preventDefault(); setSaving(true); setError('');
    const payload = { ...form, description: form.description || undefined };
    try {
      if (editing === 'new') await api.post('/admin/automations', payload);
      else if (editing) await api.put(`/admin/automations/${editing}`, payload);
      cancelEdit(); await load();
    } catch (e) { setError(e instanceof Error ? e.message : t('adm.auto.saveFailed')); }
    finally { setSaving(false); }
  }

  async function remove(id: string) {
    if (!await confirm({ title: t('adm.auto.confirmTitle'), description: t('adm.auto.confirmDesc'), confirmLabel: t('adm.auto.confirmLabel') })) return;
    try { await api.delete(`/admin/automations/${id}`); await load(); }
    catch (e) { setError(e instanceof Error ? e.message : t('adm.auto.deleteFailed')); }
  }

  async function toggleActive(rule: AutomationRule) {
    try { await api.put(`/admin/automations/${rule.id}`, { isActive: !rule.isActive }); await load(); }
    catch (e) { setError(e instanceof Error ? e.message : t('adm.auto.toggleFailed')); }
  }

  // --- Reorder ---

  async function move(index: number, direction: -1 | 1) {
    const target = index + direction;
    if (target < 0 || target >= rules.length) return;
    const reordered = [...rules];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    setRules(reordered);
    try { await api.put('/admin/automations/reorder', { ids: reordered.map((r) => r.id) }); }
    catch (e) { setError(e instanceof Error ? e.message : t('adm.auto.reorderFailed')); await load(); }
  }

  // --- Execution log ---

  async function openLog(ruleId: string) {
    setLogRuleId(ruleId); setEditing(null); setLogLoading(true);
    try {
      const data = await api.get<Execution[]>(`/admin/automations/${ruleId}/executions`);
      setExecutions(Array.isArray(data) ? data : []);
    } catch (e) { setError(e instanceof Error ? e.message : t('adm.auto.execFailed')); }
    finally { setLogLoading(false); }
  }

  // --- Condition/Action builders ---

  function updateCondition(idx: number, patch: Partial<Condition>) {
    setForm((f) => ({ ...f, conditions: f.conditions.map((c, i) => i === idx ? { ...c, ...patch } : c) }));
  }
  function addCondition() { setForm((f) => ({ ...f, conditions: [...f.conditions, { field: 'status', operator: 'is', value: '' }] })); }
  function removeCondition(idx: number) { setForm((f) => ({ ...f, conditions: f.conditions.filter((_, i) => i !== idx) })); }

  function updateAction(idx: number, patch: Partial<Action>) {
    setForm((f) => ({ ...f, actions: f.actions.map((a, i) => i === idx ? { ...a, ...patch } : a) }));
  }
  function addAction() { setForm((f) => ({ ...f, actions: [...f.actions, { type: 'set_priority', value: '' }] })); }
  function removeAction(idx: number) { setForm((f) => ({ ...f, actions: f.actions.filter((_, i) => i !== idx) })); }

  // --- Render ---

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        className="mb-0"
        breadcrumb={[{ label: t('nav.settings'), to: '/settings' }, { label: t('adm.auto.title') }]}
        title={t('adm.auto.title')}
        description={t('adm.auto.description')}
        actions={(
          <>
            <Button variant="outline" onClick={() => void load()}>{t('adm.refresh')}</Button>
            <Button onClick={startCreate}>{t('adm.auto.addRule')}</Button>
          </>
        )}
      />

      {error && <ErrorState message={error} onRetry={() => void load()} />}

      {/* Inline form */}
      {editing !== null && (
        <Card>
          <CardHeader><CardTitle>{editing === 'new' ? t('adm.auto.newRule') : t('adm.auto.editRule')}</CardTitle></CardHeader>
          <CardContent>
            <form className="space-y-4" onSubmit={save}>
              <div className="grid gap-4 md:grid-cols-2">
                <Input label={t('adm.nameRequired')} value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} required />
                <div>
                  <label className="block text-sm font-medium text-ink-muted mb-1">{t('adm.auto.trigger')}</label>
                  <DropdownSelect
                    options={TRIGGERS.map((trg) => ({ value: trg.value, label: tOr(`adm.auto.trigger.${trg.value}`, trg.label) }))}
                    value={form.trigger}
                    onChange={(value) => setForm((f) => ({ ...f, trigger: value as FormState['trigger'] }))}
                    searchable={false}
                  />
                </div>
                <div className="md:col-span-2">
                  <Input label={t('adm.descriptionLabel')} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
                </div>
              </div>

              {/* Conditions */}
              <div className="border-t pt-4">
                <h3 className="text-sm font-semibold text-ink-muted mb-2">{t('adm.auto.conditions')}</h3>
                {form.conditions.map((cond, idx) => (
                  <div key={idx} className="flex gap-2 mb-2 items-end">
                    <DropdownSelect
                      options={CONDITION_FIELDS.map((fld) => ({ value: fld, label: tOr(`adm.auto.field.${fld}`, fld) }))}
                      value={cond.field}
                      onChange={(value) => updateCondition(idx, { field: value })}
                      className="w-auto"
                    />
                    <DropdownSelect
                      options={OPERATORS.map((o) => ({ value: o, label: tOr(`adm.auto.op.${o}`, o.replace(/_/g, ' ')) }))}
                      value={cond.operator}
                      onChange={(value) => updateCondition(idx, { operator: value })}
                      className="w-auto"
                    />
                    <input className="flex-1 rounded-md border border-line-strong px-2 py-1.5 text-sm" placeholder={t('adm.auto.valuePlaceholder')} value={cond.value} onChange={(e) => updateCondition(idx, { value: e.target.value })} />
                    <Button type="button" size="sm" variant="outline" onClick={() => removeCondition(idx)} disabled={form.conditions.length <= 1}>&times;</Button>
                  </div>
                ))}
                <Button type="button" size="sm" variant="outline" onClick={addCondition}>{t('adm.auto.addCondition')}</Button>
              </div>

              {/* Actions */}
              <div className="border-t pt-4">
                <h3 className="text-sm font-semibold text-ink-muted mb-2">{t('adm.auto.actions')}</h3>
                {form.actions.map((act, idx) => (
                  <div key={idx} className="flex gap-2 mb-2 items-end">
                    <DropdownSelect
                      options={ACTION_TYPES.map((at) => ({ value: at, label: tOr(`adm.auto.action.${at}`, at.replace(/_/g, ' ')) }))}
                      value={act.type}
                      onChange={(value) => updateAction(idx, { type: value })}
                      searchable={false}
                      className="w-auto"
                    />
                    <input className="flex-1 rounded-md border border-line-strong px-2 py-1.5 text-sm" placeholder={t('adm.auto.valuePlaceholder')} value={act.value} onChange={(e) => updateAction(idx, { value: e.target.value })} />
                    <Button type="button" size="sm" variant="outline" onClick={() => removeAction(idx)} disabled={form.actions.length <= 1}>&times;</Button>
                  </div>
                ))}
                <Button type="button" size="sm" variant="outline" onClick={addAction}>{t('adm.auto.addAction')}</Button>
              </div>

              {/* Checkboxes */}
              <div className="border-t pt-4 flex items-center gap-6">
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.isActive} onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))} className="h-4 w-4 rounded border-line-strong" /> {t('adm.active')}
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.stopProcessing} onChange={(e) => setForm((f) => ({ ...f, stopProcessing: e.target.checked }))} className="h-4 w-4 rounded border-line-strong" /> {t('adm.auto.stopProcessing')}
                </label>
              </div>

              <div className="flex gap-2 pt-2">
                <Button type="submit" disabled={saving}>{saving ? t('common.saving') : editing === 'new' ? t('adm.auto.create') : t('adm.saveChanges')}</Button>
                <Button type="button" variant="outline" onClick={cancelEdit}>{t('common.cancel')}</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Rules table */}
      {loading ? <Card><CardContent className="p-0"><SkeletonRow rows={4} /></CardContent></Card> : (
        <Card>
          <CardHeader><CardTitle>{t(rules.length === 1 ? 'adm.auto.countOne' : 'adm.auto.countMany', { count: rules.length })}</CardTitle></CardHeader>
          <CardContent className="p-0">
            {rules.length === 0 ? <EmptyState title={t('adm.auto.createFirst')} description={t('adm.auto.createFirstDesc')} /> : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-16">#</TableHead>
                    <TableHead>{t('common.name')}</TableHead>
                    <TableHead>{t('adm.auto.triggerCol')}</TableHead>
                    <TableHead className="text-right">{t('adm.auto.hits')}</TableHead>
                    <TableHead>{t('adm.status')}</TableHead>
                    <TableHead>{t('common.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rules.map((rule, idx) => (
                    <TableRow key={rule.id}>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-ink-subtle w-4">{idx + 1}</span>
                          <button type="button" className="text-ink-subtle hover:text-ink-muted disabled:opacity-30" disabled={idx === 0} onClick={() => void move(idx, -1)} aria-label={t('adm.auto.moveUp')}>&#9650;</button>
                          <button type="button" className="text-ink-subtle hover:text-ink-muted disabled:opacity-30" disabled={idx === rules.length - 1} onClick={() => void move(idx, 1)} aria-label={t('adm.auto.moveDown')}>&#9660;</button>
                        </div>
                      </TableCell>
                      <TableCell className="font-medium">{rule.name}</TableCell>
                      <TableCell className="text-sm text-ink-muted">{tOr(`adm.auto.trigger.${rule.trigger}`, rule.trigger.replace(/_/g, ' '))}</TableCell>
                      <TableCell className="text-right text-sm">{rule.hitCount}</TableCell>
                      <TableCell>
                        <button type="button" onClick={() => void toggleActive(rule)} className={rule.isActive ? 'text-success-700 text-xs font-medium' : 'text-ink-subtle text-xs'}>
                          {rule.isActive ? t('adm.active') : t('adm.inactive')}
                        </button>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button size="sm" variant="outline" onClick={() => startEdit(rule)}>{t('adm.edit')}</Button>
                          <Button size="sm" variant="outline" onClick={() => void openLog(rule.id)}>{t('adm.auto.log')}</Button>
                          <Button size="sm" variant="destructive" onClick={() => void remove(rule.id)}>{t('common.delete')}</Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {/* Execution log panel */}
      {logRuleId && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>{t('adm.auto.logTitle', { name: rules.find((r) => r.id === logRuleId)?.name ?? logRuleId })}</CardTitle>
              <Button size="sm" variant="outline" onClick={() => setLogRuleId(null)}>{t('adm.close')}</Button>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {logLoading ? <SkeletonRow rows={3} /> : executions.length === 0 ? <EmptyState title={t('adm.auto.noRuns')} description={t('adm.auto.noRunsDesc')} /> : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('adm.auto.colTicket')}</TableHead>
                    <TableHead>{t('adm.auto.colSubject')}</TableHead>
                    <TableHead>{t('adm.auto.colMatched')}</TableHead>
                    <TableHead>{t('adm.auto.colApplied')}</TableHead>
                    <TableHead>{t('adm.auto.colDate')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {executions.map((ex) => (
                    <TableRow key={ex.id}>
                      <TableCell className="font-mono text-xs">{ex.ticket?.ticketNumber ?? ex.ticketId}</TableCell>
                      <TableCell className="text-sm">{ex.ticket?.subject ?? '\u2014'}</TableCell>
                      <TableCell>{ex.matched ? <span className="text-success-700 text-xs">{t('adm.auto.yes')}</span> : <span className="text-ink-subtle text-xs">{t('adm.auto.no')}</span>}</TableCell>
                      <TableCell className="text-xs text-ink-muted">{Array.isArray(ex.actionsApplied) ? ex.actionsApplied.join(', ') : '\u2014'}</TableCell>
                      <TableCell className="text-xs text-ink-subtle">{formatDateTime(ex.createdAt, user?.timezone)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
